import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHero } from "@/components/public/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatDate, STATUS_COLORS } from "@/lib/helpers";

const STEPS = ["Diterima", "Diverifikasi", "Diproses", "Ditindaklanjuti", "Selesai"];

export default function StatusAspirasi() {
  const [ticket, setTicket] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const check = async (e) => {
    e.preventDefault();
    if (!ticket.trim()) return;
    setLoading(true); setNotFound(false); setResult(null);
    try {
      const { data } = await api.get(`/aspirations/status/${ticket.trim()}`);
      setResult(data);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const activeIdx = result ? STEPS.indexOf(result.status) : -1;

  return (
    <div>
      <PageHero eyebrow="Suara Masyarakat" title="Cek Status Aspirasi"
        description="Masukkan nomor tiket aspirasi Anda untuk memantau tindak lanjut oleh Bawaslu." />
      <div className="container-page py-10 max-w-2xl">
        <Link to="/suara-masyarakat" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-brandred mb-6">
          <ArrowLeft className="h-4 w-4" /> Kembali
        </Link>
        <form onSubmit={check} className="flex gap-2 mb-8" data-testid="ticket-checker">
          <div className="flex items-center flex-1 bg-white border border-slate-200 rounded-lg px-3">
            <Search className="h-4 w-4 text-slate-400" />
            <Input data-testid="ticket-input" value={ticket} onChange={(e) => setTicket(e.target.value)}
              placeholder="Contoh: HMB-2026-00001" className="border-0 shadow-none focus-visible:ring-0 font-mono uppercase" />
          </div>
          <Button type="submit" disabled={loading} className="bg-brandred hover:bg-brandred-700 text-white" data-testid="ticket-check-btn">
            {loading ? "Mengecek…" : "Cek Status"}
          </Button>
        </form>

        {notFound && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center" data-testid="ticket-notfound">
            <p className="text-brandred font-semibold">Nomor tiket tidak ditemukan.</p>
            <p className="text-sm text-slate-500 mt-1">Pastikan nomor tiket yang Anda masukkan benar.</p>
          </div>
        )}

        {result && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="ticket-result">
            <div className="flex items-center justify-between flex-wrap gap-3 pb-5 border-b border-slate-100">
              <div>
                <div className="text-xs text-slate-500 uppercase">Nomor Tiket</div>
                <div className="text-xl font-extrabold text-navy font-mono">{result.ticket}</div>
              </div>
              <span className={`text-sm font-bold px-3 py-1.5 rounded-full border ${STATUS_COLORS[result.status]}`}>{result.status}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 py-5 text-sm">
              <div><span className="text-slate-400">Jenis:</span> <span className="font-semibold text-navy">{result.type}</span></div>
              <div><span className="text-slate-400">Tanggal Masuk:</span> <span className="font-semibold text-navy">{formatDate(result.created_at)}</span></div>
              <div className="sm:col-span-2"><span className="text-slate-400">Subjek:</span> <span className="font-semibold text-navy">{result.subject}</span></div>
            </div>

            <div className="py-4">
              <div className="text-xs font-bold uppercase text-slate-400 mb-4">Perkembangan Status</div>
              <div className="space-y-0">
                {STEPS.map((s, i) => (
                  <div key={s} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${i <= activeIdx ? "bg-brandred text-white" : "bg-slate-100 text-slate-400"}`}>{i + 1}</div>
                      {i < STEPS.length - 1 && <div className={`w-0.5 h-8 ${i < activeIdx ? "bg-brandred" : "bg-slate-100"}`} />}
                    </div>
                    <div className={`pt-0.5 text-sm font-semibold ${i <= activeIdx ? "text-navy" : "text-slate-400"}`}>{s}</div>
                  </div>
                ))}
              </div>
            </div>

            {result.response && (
              <div className="bg-surface rounded-xl p-4 mt-4">
                <div className="text-xs font-bold uppercase text-slate-400 mb-1">Tanggapan Bawaslu</div>
                <p className="text-sm text-slate-700">{result.response}</p>
              </div>
            )}
          </div>
        )}
        <p className="text-xs text-slate-400 mt-6 text-center">Contoh tiket demo: <span className="font-mono">HMB-2026-00001</span> s.d. <span className="font-mono">HMB-2026-00010</span></p>
      </div>
    </div>
  );
}
